import React, { useState } from 'react';
import {
  Bell, Mail, MessageSquare, Smartphone, CheckCircle, Clock,
  AlertTriangle, Filter, Search, Eye, RefreshCw
} from 'lucide-react';

const notificationsData = [
  {
    id: '1',
    type: 'invoice',
    channel: 'email',
    recipient: 'FastShip Jamaica',
    email: 'marcus@fastship.jm',
    subject: 'Invoice INV-2026-4521 Ready',
    message: 'Your invoice for $85,420 is ready for payment.',
    status: 'delivered',
    sentAt: '2026-02-28 10:45 AM'
  },
  {
    id: '2',
    type: 'payment',
    channel: 'whatsapp',
    recipient: 'SpeedyCourier',
    phone: '+1 876-555-0456',
    subject: 'Payment Confirmation',
    message: 'Payment of $42,150 received. Thank you!',
    status: 'delivered',
    sentAt: '2026-02-28 09:30 AM'
  },
  {
    id: '3',
    type: 'reminder',
    channel: 'sms',
    recipient: 'IslandExpress',
    phone: '+1 876-555-0789',
    subject: 'Payment Reminder',
    message: 'Invoice INV-2026-4519 due in 2 days.',
    status: 'pending',
    sentAt: '2026-02-28 08:00 AM'
  },
  {
    id: '4',
    type: 'pickup',
    channel: 'whatsapp',
    recipient: 'KP Direct Customer',
    phone: '+1 876-555-2222',
    subject: 'Package Ready',
    message: 'Your package KP-PKG-4521 is ready for pickup!',
    status: 'delivered',
    sentAt: '2026-02-27 04:15 PM'
  },
  {
    id: '5',
    type: 'invoice',
    channel: 'email',
    recipient: 'IslandExpress',
    email: 'david@islandexpress.jm',
    subject: 'Invoice INV-2026-4519 Ready',
    message: 'Your invoice for $38,029 is ready.',
    status: 'failed',
    sentAt: '2026-02-27 02:30 PM',
    error: 'Email bounced - invalid address'
  },
];

const channelIcons = {
  email: Mail,
  whatsapp: MessageSquare,
  sms: Smartphone
};

const channelColors = {
  email: '#3b82f6',
  whatsapp: '#22c55e',
  sms: '#f59e0b'
};

export default function Notifications() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChannel, setFilterChannel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredNotifications = notificationsData.filter(n => {
    const matchesSearch = n.recipient.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         n.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesChannel = filterChannel === 'all' || n.channel === filterChannel;
    const matchesStatus = filterStatus === 'all' || n.status === filterStatus;
    return matchesSearch && matchesChannel && matchesStatus;
  });

  const deliveredCount = notificationsData.filter(n => n.status === 'delivered').length;
  const pendingCount = notificationsData.filter(n => n.status === 'pending').length;
  const failedCount = notificationsData.filter(n => n.status === 'failed').length;

  return (
    <div className="notifications-page" data-testid="notifications-page">
      {/* Summary */}
      <div className="notification-summary-grid">
        <div className="summary-card delivered">
          <CheckCircle size={24} />
          <div className="summary-content">
            <span className="summary-value">{deliveredCount}</span>
            <span className="summary-label">Delivered</span>
          </div>
        </div>
        <div className="summary-card pending">
          <Clock size={24} />
          <div className="summary-content">
            <span className="summary-value">{pendingCount}</span>
            <span className="summary-label">Pending</span>
          </div>
        </div>
        <div className="summary-card failed">
          <AlertTriangle size={24} />
          <div className="summary-content">
            <span className="summary-value">{failedCount}</span>
            <span className="summary-label">Failed</span>
          </div>
        </div>
      </div>

      {/* Channel Stats */}
      <div className="channel-stats">
        <div className="channel-stat" style={{ borderColor: channelColors.email }}>
          <Mail size={20} style={{ color: channelColors.email }} />
          <span className="channel-count">{notificationsData.filter(n => n.channel === 'email').length}</span>
          <span className="channel-label">Emails</span>
        </div>
        <div className="channel-stat" style={{ borderColor: channelColors.whatsapp }}>
          <MessageSquare size={20} style={{ color: channelColors.whatsapp }} />
          <span className="channel-count">{notificationsData.filter(n => n.channel === 'whatsapp').length}</span>
          <span className="channel-label">WhatsApp</span>
        </div>
        <div className="channel-stat" style={{ borderColor: channelColors.sms }}>
          <Smartphone size={20} style={{ color: channelColors.sms }} />
          <span className="channel-count">{notificationsData.filter(n => n.channel === 'sms').length}</span>
          <span className="channel-label">SMS</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="notification-search"
          />
        </div>
        <div className="toolbar-right">
          <select 
            className="status-filter"
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            data-testid="channel-filter"
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
          </select>
          <select 
            className="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="notification-status-filter"
          >
            <option value="all">All Status</option>
            <option value="delivered">Delivered</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <div className="notifications-list">
        {filteredNotifications.map((notification) => {
          const ChannelIcon = channelIcons[notification.channel];
          return (
            <div 
              key={notification.id} 
              className={`notification-card ${notification.status}`}
              data-testid={`notification-${notification.id}`}
            >
              <div 
                className="notification-channel"
                style={{ backgroundColor: channelColors[notification.channel] + '20' }}
              >
                <ChannelIcon size={20} style={{ color: channelColors[notification.channel] }} />
              </div>
              
              <div className="notification-content">
                <div className="notification-header">
                  <span className="notification-recipient">{notification.recipient}</span>
                  <span className="notification-time">{notification.sentAt}</span>
                </div>
                <h4 className="notification-subject">{notification.subject}</h4>
                <p className="notification-message">{notification.message}</p>
                {notification.error && (
                  <p className="notification-error">
                    <AlertTriangle size={12} /> {notification.error}
                  </p>
                )}
                <div className="notification-meta">
                  <span className="notification-contact">
                    {notification.email || notification.phone}
                  </span>
                  <span className={`notification-type ${notification.type}`}>
                    {notification.type}
                  </span>
                </div>
              </div>

              <div className="notification-status">
                <span className={`status-indicator ${notification.status}`}>
                  {notification.status === 'delivered' && <CheckCircle size={16} />}
                  {notification.status === 'pending' && <Clock size={16} />}
                  {notification.status === 'failed' && <AlertTriangle size={16} />}
                  {notification.status}
                </span>
                {notification.status === 'failed' && (
                  <button className="retry-btn" data-testid={`retry-${notification.id}`}>
                    <RefreshCw size={14} /> Retry
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="table-pagination">
        <span className="pagination-info">Showing 1-5 of 128 notifications</span>
        <div className="pagination-controls">
          <button className="pagination-btn" disabled>Previous</button>
          <button className="pagination-btn active">1</button>
          <button className="pagination-btn">2</button>
          <button className="pagination-btn">3</button>
          <span>...</span>
          <button className="pagination-btn">26</button>
          <button className="pagination-btn">Next</button>
        </div>
      </div>
    </div>
  );
}
